// Size normalization utility based on comprehensive size filter configuration
// Version: 1.0.0

export type NormalizedSize = 'small' | 'regular' | 'large';

interface SizeMetadata {
  volume_oz: number | null;
  shots: number | null;
  container: 'standard' | 'reusable';
  fill_level: string | null;
}

interface NormalizedSizeEntry {
  raw_label: string;
  bucket: NormalizedSize | null;
  price: number | string | null;
  metadata: SizeMetadata;
}

interface SizeNormalizerConfig {
  version: string;
  purpose: string;
  ui_sizes: NormalizedSize[];
  ui_default: NormalizedSize;
  normalization: {
    string_preprocess: {
      trim: boolean;
      collapse_whitespace: boolean;
      casefold: boolean;
      replace: Array<{ from: string; to: string }>;
    };
    tokenization: {
      strip_punctuation: boolean;
      keep_parentheses_content: boolean;
    };
  };
  exclude_from_size_dimension: {
    fill_level_patterns: Array<{
      id: string;
      regex_ci: string;
      extract: { fill_level: string };
    }>;
    container_patterns: Array<{
      id: string;
      regex_ci: string;
      extract: { container: string };
    }>;
    shots_patterns: Array<{
      id: string;
      regex_ci: string;
      extract: { shots: string };
    }>;
    volume_patterns: Array<{
      id: string;
      regex_ci: string;
      extract: { volume_oz: string };
    }>;
  };
  size_label_mapping: {
    direct_label_map: Array<{
      match_any_ci: string[];
      to: NormalizedSize;
    }>;
    volume_oz_to_bucket: {
      enabled: boolean;
      rules: Array<{
        if_volume_oz_lte?: number;
        if_volume_oz_gte?: number;
        and_lte?: number;
        to: NormalizedSize;
      }>;
    };
  };
}

const config: SizeNormalizerConfig = {
  version: '1.0.0',
  purpose: 'Normalize venue-specific coffee size labels into a simple UI filter: Small / Regular / Large, with predictable fallback behavior.',
  ui_sizes: ['small', 'regular', 'large'],
  ui_default: 'regular',
  normalization: {
    string_preprocess: {
      trim: true,
      collapse_whitespace: true,
      casefold: true,
      replace: [
        { from: '–', to: '-' },
        { from: '—', to: '-' },
        { from: '_', to: ' ' },
      ],
    },
    tokenization: {
      strip_punctuation: true,
      keep_parentheses_content: true,
    },
  },
  exclude_from_size_dimension: {
    fill_level_patterns: [
      {
        id: 'fill_level_fraction_full',
        regex_ci: '^\\s*(\\d)\\s*(/|\\s)\\s*(\\d)\\s*full\\s*$',
        extract: { fill_level: '$1/$3' },
      },
      {
        id: 'fill_level_dash_full',
        regex_ci: '^\\s*(\\d)\\s*[-]\\s*(\\d)\\s*full\\s*$',
        extract: { fill_level: '$1/$2' },
      },
    ],
    container_patterns: [
      {
        id: 'reusable_cup',
        regex_ci: '\\b(re-?usable|keep\\s?cup|bring\\s?your\\s?own)\\b',
        extract: { container: 'reusable' },
      },
    ],
    shots_patterns: [
      {
        id: 'shots_in_parentheses',
        regex_ci: '\\((\\d+)\\s*shots?\\)',
        extract: { shots: '$1' },
      },
      {
        id: 'shots_inline',
        regex_ci: '\\b(\\d+)\\s*shots?\\b',
        extract: { shots: '$1' },
      },
    ],
    volume_patterns: [
      {
        id: 'oz_parentheses',
        regex_ci: '\\((\\d+(?:\\.\\d+)?)\\s*oz\\)',
        extract: { volume_oz: '$1' },
      },
      {
        id: 'oz_inline',
        regex_ci: '\\b(\\d+(?:\\.\\d+)?)\\s*oz\\b',
        extract: { volume_oz: '$1' },
      },
    ],
  },
  size_label_mapping: {
    direct_label_map: [
      { match_any_ci: ['small', 'sml', 'sm', 'short', 'petite'], to: 'small' },
      { match_any_ci: ['medium', 'med', 'm', 'mid'], to: 'regular' },
      { match_any_ci: ['regular', 'reg', 'standard', 'std', 'normal'], to: 'regular' },
      { match_any_ci: ['large', 'lrg', 'lg', 'big'], to: 'large' },
      { match_any_ci: ['extra large', 'xlarge', 'xl', 'mega', 'jumbo', 'king', 'giant'], to: 'large' },
    ],
    volume_oz_to_bucket: {
      enabled: true,
      rules: [
        { if_volume_oz_lte: 8, to: 'small' },
        { if_volume_oz_gte: 9, and_lte: 13, to: 'regular' },
        { if_volume_oz_gte: 14, to: 'large' },
      ],
    },
  },
};

// String preprocessing
function preprocessString(input: string): string {
  if (!input || typeof input !== 'string') return '';
  let result = input;

  // Apply replacements
  for (const replacement of config.normalization.string_preprocess.replace) {
    result = result.replace(new RegExp(replacement.from, 'g'), replacement.to);
  }

  // Casefold
  if (config.normalization.string_preprocess.casefold) {
    result = result.toLowerCase();
  }

  // Collapse whitespace
  if (config.normalization.string_preprocess.collapse_whitespace) {
    result = result.replace(/\s+/g, ' ');
  }

  // Trim
  if (config.normalization.string_preprocess.trim) {
    result = result.trim();
  }

  return result;
}

// Extract metadata from label
function extractMetadata(label: string): SizeMetadata {
  const metadata: SizeMetadata = {
    volume_oz: null,
    shots: null,
    container: 'standard',
    fill_level: null,
  };

  const preprocessed = preprocessString(label);

  // Extract container
  for (const pattern of config.exclude_from_size_dimension.container_patterns) {
    const regex = new RegExp(pattern.regex_ci, 'i');
    const match = preprocessed.match(regex);
    if (match) {
      if (pattern.extract.container === 'reusable') {
        metadata.container = 'reusable';
      }
    }
  }

  // Extract volume
  for (const pattern of config.exclude_from_size_dimension.volume_patterns) {
    const regex = new RegExp(pattern.regex_ci, 'i');
    const match = preprocessed.match(regex);
    if (match && match[1]) {
      const volume = parseFloat(match[1]);
      if (!isNaN(volume)) {
        metadata.volume_oz = volume;
      }
    }
  }

  // Extract shots
  for (const pattern of config.exclude_from_size_dimension.shots_patterns) {
    const regex = new RegExp(pattern.regex_ci, 'i');
    const match = preprocessed.match(regex);
    if (match && match[1]) {
      const shots = parseInt(match[1], 10);
      if (!isNaN(shots)) {
        metadata.shots = shots;
      }
    }
  }

  // Extract fill level
  for (const pattern of config.exclude_from_size_dimension.fill_level_patterns) {
    const regex = new RegExp(pattern.regex_ci, 'i');
    const match = preprocessed.match(regex);
    if (match) {
      let fillLevel = pattern.extract.fill_level;
      if (fillLevel === '$1/$3' && match[1] && match[3]) {
        fillLevel = `${match[1]}/${match[3]}`;
      } else if (fillLevel === '$1/$2' && match[1] && match[2]) {
        fillLevel = `${match[1]}/${match[2]}`;
      }
      metadata.fill_level = fillLevel;
    }
  }

  return metadata;
}

// Remove extracted noise tokens
function removeExtractedNoiseTokens(label: string): string {
  let result = label;
  const removePatterns = [
    '\\b(re-?usable|keep\\s?cup|bring\\s?your\\s?own)\\b',
    '\\((\\d+(?:\\.\\d+)?)\\s*oz\\)',
    '\\b(\\d+(?:\\.\\d+)?)\\s*oz\\b',
    '\\((\\d+)\\s*shots?\\)',
    '\\b(\\d+)\\s*shots?\\b',
    '^\\s*(\\d)\\s*(/|\\s)\\s*(\\d)\\s*full\\s*$',
    '^\\s*(\\d)\\s*[-]\\s*(\\d)\\s*full\\s*$',
  ];

  for (const pattern of removePatterns) {
    const regex = new RegExp(pattern, 'gi');
    result = result.replace(regex, '');
  }

  // Trim after removal
  result = result.trim().replace(/\s+/g, ' ');

  return result;
}

// Map to bucket using direct label map
function mapDirectLabel(cleanedLabel: string): NormalizedSize | null {
  const preprocessed = preprocessString(cleanedLabel);

  for (const mapping of config.size_label_mapping.direct_label_map) {
    for (const matchTerm of mapping.match_any_ci) {
      const normalizedMatch = preprocessString(matchTerm);
      // Check for full match or token match
      if (preprocessed === normalizedMatch) {
        return mapping.to;
      }
      // Token-based matching (contains any token)
      const tokens = preprocessed.split(/\s+/);
      const matchTokens = normalizedMatch.split(/\s+/);
      for (const token of tokens) {
        if (matchTokens.includes(token)) {
          return mapping.to;
        }
      }
    }
  }

  return null;
}

// Map to bucket using volume
function mapVolumeToBucket(volumeOz: number): NormalizedSize | null {
  if (!config.size_label_mapping.volume_oz_to_bucket.enabled) {
    return null;
  }

  for (const rule of config.size_label_mapping.volume_oz_to_bucket.rules) {
    if (rule.if_volume_oz_lte !== undefined && volumeOz <= rule.if_volume_oz_lte) {
      return rule.to;
    }
    if (
      rule.if_volume_oz_gte !== undefined &&
      rule.and_lte !== undefined &&
      volumeOz >= rule.if_volume_oz_gte &&
      volumeOz <= rule.and_lte
    ) {
      return rule.to;
    }
    if (rule.if_volume_oz_gte !== undefined && !rule.and_lte && volumeOz >= rule.if_volume_oz_gte) {
      return rule.to;
    }
  }

  return null;
}

// Normalize a single size label to a bucket
export function normalizeSizeLabel(label: string): NormalizedSize | null {
  if (!label || typeof label !== 'string') return null;

  // Step 1: Preprocess string
  const preprocessed = preprocessString(label);

  // Step 2: Extract metadata
  const metadata = extractMetadata(label);

  // Step 3: Remove extracted noise tokens
  const cleanedLabel = removeExtractedNoiseTokens(label);

  // Step 4: Map to bucket
  // Try direct label map first
  let bucket = mapDirectLabel(cleanedLabel);

  // If no bucket yet and we have volume, try volume mapping
  if (!bucket && metadata.volume_oz !== null) {
    bucket = mapVolumeToBucket(metadata.volume_oz);
  }

  // If still no bucket, return null (don't default to regular)
  return bucket;
}

// Normalize a size entry with metadata
function normalizeSizeEntry(rawLabel: string, price: number | string | null): NormalizedSizeEntry {
  const metadata = extractMetadata(rawLabel);
  const bucket = normalizeSizeLabel(rawLabel);

  return {
    raw_label: rawLabel,
    bucket,
    price,
    metadata,
  };
}

// Normalize menu item size set
export function normalizeMenuItemSizes(
  sizes: Record<string, number | string | null>
): {
  normalized_sizes: NormalizedSizeEntry[];
  available_buckets: Set<NormalizedSize>;
} {
  const normalizedSizes: NormalizedSizeEntry[] = [];
  const availableBuckets = new Set<NormalizedSize>();

  // Normalize each size entry
  for (const [rawLabel, price] of Object.entries(sizes)) {
    // Skip metadata keys
    if (rawLabel === '_typeId' || rawLabel === '_categoryId' || rawLabel === 'typeId' || rawLabel === 'categoryId') {
      continue;
    }

    // Skip non-numeric prices (unless it's a string that represents a price)
    if (typeof price !== 'number' && price !== null && typeof price !== 'string') {
      continue;
    }

    const entry = normalizeSizeEntry(rawLabel, price);

    // Drop entries that are pure fill-level options
    if (entry.bucket === null && entry.metadata.fill_level !== null) {
      continue;
    }

    normalizedSizes.push(entry);

    if (entry.bucket !== null) {
      availableBuckets.add(entry.bucket);
    }
  }

  return {
    normalized_sizes: normalizedSizes,
    available_buckets: availableBuckets,
  };
}

// UI price selection logic
export function selectUIPrice(
  normalizedSizes: NormalizedSizeEntry[],
  selectedBucket: NormalizedSize
): number | string | null {
  if (normalizedSizes.length === 0) return null;

  const availableBuckets = new Set<NormalizedSize>();
  const bucketEntries = new Map<NormalizedSize, NormalizedSizeEntry[]>();

  for (const entry of normalizedSizes) {
    if (entry.bucket !== null) {
      availableBuckets.add(entry.bucket);
      if (!bucketEntries.has(entry.bucket)) {
        bucketEntries.set(entry.bucket, []);
      }
      bucketEntries.get(entry.bucket)!.push(entry);
    }
  }

  // Rule 1: Exact bucket match with standard container
  const exactStandard = bucketEntries
    .get(selectedBucket)
    ?.find((e) => e.metadata.container === 'standard');
  if (exactStandard) {
    return exactStandard.price;
  }

  // Rule 2: Fallback to regular if available
  if (selectedBucket !== 'regular' && availableBuckets.has('regular')) {
    const regularStandard = bucketEntries
      .get('regular')
      ?.find((e) => e.metadata.container === 'standard');
    if (regularStandard) {
      return regularStandard.price;
    }
  }

  // Rule 3: Single bucket only
  if (availableBuckets.size === 1) {
    const onlyBucket = Array.from(availableBuckets)[0];
    const standardEntry = bucketEntries
      .get(onlyBucket)
      ?.find((e) => e.metadata.container === 'standard');
    if (standardEntry) {
      return standardEntry.price;
    }
    // Fallback to any entry in that bucket
    const anyEntry = bucketEntries.get(onlyBucket)?.[0];
    if (anyEntry) {
      return anyEntry.price;
    }
  }

  // Rule 4: Two-size sets (small + large)
  if (availableBuckets.size === 2 && availableBuckets.has('small') && availableBuckets.has('large')) {
    if (selectedBucket === 'small' || selectedBucket === 'regular') {
      const smallStandard = bucketEntries
        .get('small')
        ?.find((e) => e.metadata.container === 'standard');
      if (smallStandard) {
        return smallStandard.price;
      }
    } else if (selectedBucket === 'large') {
      const largeStandard = bucketEntries
        .get('large')
        ?.find((e) => e.metadata.container === 'standard');
      if (largeStandard) {
        return largeStandard.price;
      }
    }
  }

  // Rule 5: Missing small (regular + large)
  if (availableBuckets.size === 2 && availableBuckets.has('regular') && availableBuckets.has('large')) {
    if (selectedBucket === 'small' || selectedBucket === 'regular') {
      const regularStandard = bucketEntries
        .get('regular')
        ?.find((e) => e.metadata.container === 'standard');
      if (regularStandard) {
        return regularStandard.price;
      }
    } else if (selectedBucket === 'large') {
      const largeStandard = bucketEntries
        .get('large')
        ?.find((e) => e.metadata.container === 'standard');
      if (largeStandard) {
        return largeStandard.price;
      }
    }
  }

  // Rule 6: Otherwise, use lowest-priced standard-container bucket
  let lowestPrice: number | null = null;
  let lowestEntry: NormalizedSizeEntry | null = null;

  for (const entry of normalizedSizes) {
    if (entry.bucket !== null && entry.metadata.container === 'standard' && entry.price !== null) {
      const priceValue = typeof entry.price === 'number' ? entry.price : parseFloat(entry.price);
      if (!isNaN(priceValue) && (lowestPrice === null || priceValue < lowestPrice)) {
        lowestPrice = priceValue;
        lowestEntry = entry;
      }
    }
  }

  if (lowestEntry) {
    return lowestEntry.price;
  }

  // Final fallback: return first available price
  const firstEntry = normalizedSizes.find((e) => e.price !== null);
  return firstEntry?.price ?? null;
}

// Get effective regular size for a menu item (smart fallback)
export function getEffectiveRegularSize(availableSizes: string[]): NormalizedSize {
  const normalizedSizes = availableSizes
    .map((s) => normalizeSizeLabel(s))
    .filter((s): s is NormalizedSize => s !== null);

  // Rule 1: If regular is available, use it
  if (normalizedSizes.includes('regular')) {
    return 'regular';
  }

  // Rule 2: If only small+large exist, small is treated as regular
  if (normalizedSizes.length === 2 && normalizedSizes.includes('small') && normalizedSizes.includes('large')) {
    return 'small'; // This will be treated as regular
  }

  // Rule 3: If only small exists, use small
  if (normalizedSizes.length === 1 && normalizedSizes[0] === 'small') {
    return 'small';
  }

  // Rule 4: If only large exists, use large
  if (normalizedSizes.length === 1 && normalizedSizes[0] === 'large') {
    return 'large';
  }

  // Default: return first available or regular
  return normalizedSizes[0] || 'regular';
}

// Preprocess a location to normalize all size labels
export function preprocessLocationSizes(location: any): any {
  if (!location) return location;

  const processed = { ...location };

  // Process coffee_menu format
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    const processedMenu: Record<string, any> = {};

    Object.entries(location.coffee_menu).forEach(([itemName, menuItem]: [string, any]) => {
      if (typeof menuItem === 'object' && menuItem !== null) {
        const processedItem: Record<string, any> = { ...menuItem };
        const sizeEntries: Record<string, number | string | null> = {};

        // Collect all size keys and their prices
        Object.entries(menuItem).forEach(([key, value]: [string, any]) => {
          // Skip metadata keys
          if (key === '_typeId' || key === '_categoryId' || key === 'typeId' || key === 'categoryId') {
            return;
          }

          if (typeof value === 'number' || value === null || typeof value === 'string') {
            sizeEntries[key] = value;
          }
        });

        // Normalize the size set
        const { normalized_sizes, available_buckets } = normalizeMenuItemSizes(sizeEntries);

        // Remove old size keys
        Object.keys(processedItem).forEach((key) => {
          if (key !== '_typeId' && key !== '_categoryId' && key !== 'typeId' && key !== 'categoryId') {
            if (typeof processedItem[key] === 'number' || processedItem[key] === null) {
              delete processedItem[key];
            }
          }
        });

        // Add normalized sizes by bucket (keep first price for each bucket)
        const bucketPrices = new Map<NormalizedSize, number | string | null>();
        for (const entry of normalized_sizes) {
          if (entry.bucket !== null && !bucketPrices.has(entry.bucket)) {
            bucketPrices.set(entry.bucket, entry.price);
          }
        }

        // Add normalized bucket prices
        for (const [bucket, price] of Array.from(bucketPrices.entries())) {
          processedItem[bucket] = price;
        }

        // Add effective regular if needed (for backward compatibility)
        if (!bucketPrices.has('regular') && available_buckets.size > 0) {
          const availableBucketsArray = Array.from(available_buckets);
          // If only small+large exist, small is treated as regular
          if (availableBucketsArray.length === 2 && availableBucketsArray.includes('small') && availableBucketsArray.includes('large')) {
            if (bucketPrices.has('small')) {
              processedItem['regular'] = bucketPrices.get('small');
            }
          } else if (availableBucketsArray.length === 1) {
            // If only one bucket exists, use it as regular
            const onlyBucket = availableBucketsArray[0];
            if (bucketPrices.has(onlyBucket)) {
              processedItem['regular'] = bucketPrices.get(onlyBucket);
            }
          }
        }

        processedMenu[itemName] = processedItem;
      } else {
        processedMenu[itemName] = menuItem;
      }
    });

    processed.coffee_menu = processedMenu;
  }

  // Process legacy format (coffeeHotDrinks, icedCoffeeTea)
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    processed.coffeeHotDrinks = location.coffeeHotDrinks.map((drink: any) => {
      if (drink.price && typeof drink.price === 'object') {
        const { normalized_sizes, available_buckets } = normalizeMenuItemSizes(drink.price);
        const processedPrice: Record<string, any> = {};

        // Add normalized sizes by bucket
        const bucketPrices = new Map<NormalizedSize, number | string | null>();
        for (const entry of normalized_sizes) {
          if (entry.bucket !== null && !bucketPrices.has(entry.bucket)) {
            bucketPrices.set(entry.bucket, entry.price);
          }
        }

        for (const [bucket, price] of Array.from(bucketPrices.entries())) {
          processedPrice[bucket] = price;
        }

        // Add effective regular if needed
        if (!bucketPrices.has('regular') && available_buckets.size > 0) {
          const availableBucketsArray = Array.from(available_buckets);
          // If only small+large exist, small is treated as regular
          if (availableBucketsArray.length === 2 && availableBucketsArray.includes('small') && availableBucketsArray.includes('large')) {
            if (bucketPrices.has('small')) {
              processedPrice['regular'] = bucketPrices.get('small');
            }
          } else if (availableBucketsArray.length === 1) {
            // If only one bucket exists, use it as regular
            const onlyBucket = availableBucketsArray[0];
            if (bucketPrices.has(onlyBucket)) {
              processedPrice['regular'] = bucketPrices.get(onlyBucket);
            }
          }
        }

        return { ...drink, price: processedPrice };
      }
      return drink;
    });
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    processed.icedCoffeeTea = location.icedCoffeeTea.map((drink: any) => {
      if (drink.price && typeof drink.price === 'object') {
        const { normalized_sizes, available_buckets } = normalizeMenuItemSizes(drink.price);
        const processedPrice: Record<string, any> = {};

        // Add normalized sizes by bucket
        const bucketPrices = new Map<NormalizedSize, number | string | null>();
        for (const entry of normalized_sizes) {
          if (entry.bucket !== null && !bucketPrices.has(entry.bucket)) {
            bucketPrices.set(entry.bucket, entry.price);
          }
        }

        for (const [bucket, price] of Array.from(bucketPrices.entries())) {
          processedPrice[bucket] = price;
        }

        // Add effective regular if needed
        if (!bucketPrices.has('regular') && available_buckets.size > 0) {
          const availableBucketsArray = Array.from(available_buckets);
          // If only small+large exist, small is treated as regular
          if (availableBucketsArray.length === 2 && availableBucketsArray.includes('small') && availableBucketsArray.includes('large')) {
            if (bucketPrices.has('small')) {
              processedPrice['regular'] = bucketPrices.get('small');
            }
          } else if (availableBucketsArray.length === 1) {
            // If only one bucket exists, use it as regular
            const onlyBucket = availableBucketsArray[0];
            if (bucketPrices.has(onlyBucket)) {
              processedPrice['regular'] = bucketPrices.get(onlyBucket);
            }
          }
        }

        return { ...drink, price: processedPrice };
      }
      return drink;
    });
  }

  return processed;
}
